/* ==========================================================================
   GLOBAL INTERACTION SCRIPTS (VANILLA JS - NO JQUERY)
   ========================================================================== */

document.addEventListener('DOMContentLoaded', () => {
  // Initialize Page Transitions
  initPageTransitions();
  
  // Initialize standard site modules
  initAnnouncementBar();
  initStickyHeader();
  initSearchOverlay();
  initPredictiveSearch();
  initDrawers();
  initHeroSliders();
  initTestimonialsSliders();
  initScrollAnimations();
  initScrollStorytelling();
  initMegaMenu();
  
  // Page-specific modules
  if (document.querySelector('.js-product-page-wrapper')) {
    initProductPage();
    initProductRecommendations();
  }
  initQuickAdd();
  initQtyButtons();
  if (document.querySelector('.js-sort-by') || document.querySelector('.collection-filters')) {
    initCollectionFilters();
  }
});

/* ==========================================================================
   ANNOUNCEMENT BAR SLIDER
   ========================================================================== */
function initAnnouncementBar() {
  const slider = document.querySelector('.announcement-bar__slider');
  if (!slider) return;
  
  const slides = slider.querySelectorAll('.announcement-bar__slide');
  if (slides.length <= 1) return;
  
  const speed = parseInt(slider.getAttribute('data-speed')) || 5000;
  let currentSlide = 0;
  let intervalId = null;

  function showSlide(index) {
    slides[currentSlide].classList.remove('active');
    currentSlide = (index + slides.length) % slides.length;
    slides[currentSlide].classList.add('active');
  }

  function startAutoplay() {
    if (intervalId) clearInterval(intervalId);
    intervalId = setInterval(() => {
      showSlide(currentSlide + 1);
    }, speed);
  }

  const prevBtn = document.querySelector('.js-announcement-prev');
  const nextBtn = document.querySelector('.js-announcement-next');

  if (prevBtn) {
    prevBtn.addEventListener('click', (e) => {
      e.preventDefault();
      showSlide(currentSlide - 1);
      startAutoplay(); // Reset autoplay timer
    });
  }

  if (nextBtn) {
    nextBtn.addEventListener('click', (e) => {
      e.preventDefault();
      showSlide(currentSlide + 1);
      startAutoplay(); // Reset autoplay timer
    });
  }
  
  startAutoplay();
}

/* ==========================================================================
   STICKY & TRANSPARENT HEADER
   ========================================================================== */
function initStickyHeader() {
  const header = document.querySelector('.js-site-header');
  if (!header) return;
  
  const handleScroll = () => {
    if (window.scrollY > 40) {
      header.classList.add('site-header--sticky');
      if (header.classList.contains('site-header--transparent')) {
        header.classList.remove('site-header--transparent');
        header.setAttribute('data-was-transparent', 'true');
      }
    } else {
      header.classList.remove('site-header--sticky');
      if (header.getAttribute('data-was-transparent') === 'true') {
        header.classList.add('site-header--transparent');
        header.removeAttribute('data-was-transparent');
      }
    }
  };
  
  window.addEventListener('scroll', handleScroll, { passive: true });
  handleScroll(); // Trigger initial scroll check
}

/* ==========================================================================
   SEARCH OVERLAY DROPDOWN
   ========================================================================== */
function initSearchOverlay() {
  const triggers = document.querySelectorAll('.js-search-trigger');
  const overlay = document.querySelector('.js-search-overlay');
  const closeBtns = document.querySelectorAll('.js-search-close');
  const input = document.querySelector('.js-search-input');
  
  if (!triggers.length || !overlay) return;
  
  const openSearch = (e) => {
    e.preventDefault();
    overlay.classList.add('active');
    overlay.setAttribute('aria-hidden', 'false');
    document.body.style.overflow = 'hidden';
    if (input) setTimeout(() => input.focus(), 120);
  };
  
  triggers.forEach(trigger => trigger.addEventListener('click', openSearch));
  
  const closeSearch = () => {
    overlay.classList.remove('active');
    overlay.setAttribute('aria-hidden', 'true');
    document.body.style.overflow = '';
  };
  
  closeBtns.forEach(btn => btn.addEventListener('click', closeSearch));
  
  // Close search on Escape key
  document.addEventListener('keydown', (e) => {
    if (e.key === 'Escape' && overlay.classList.contains('active')) {
      closeSearch();
    }
  });
}

/* ==========================================================================
   PREDICTIVE SEARCH AUTOCOMPLETE
   ========================================================================== */
function initPredictiveSearch() {
  const searchInputs = document.querySelectorAll('.header-search-input');
  
  searchInputs.forEach(input => {
    const wrapper = input.closest('.search-input-wrapper');
    if (!wrapper) return;
    
    const resultsContainer = wrapper.querySelector('.predictive-search-results');
    if (!resultsContainer) return;
    
    let debounceTimer;
    
    input.addEventListener('input', () => {
      clearTimeout(debounceTimer);
      const query = input.value.trim();
      
      if (query.length < 2) {
        resultsContainer.innerHTML = '';
        resultsContainer.style.display = 'none';
        return;
      }
      
      debounceTimer = setTimeout(() => {
        fetch(`/search/suggest.json?q=${encodeURIComponent(query)}&resources[type]=product,page&resources[limit]=5`)
          .then(res => {
            if (!res.ok) throw new Error('Network response not ok');
            return res.json();
          })
          .then(data => {
            renderResults(data, resultsContainer);
          })
          .catch(err => {
            console.error('Error fetching predictive search suggestions:', err);
          });
      }, 250);
    });
    
    // Hide dropdown when clicking outside
    document.addEventListener('click', (e) => {
      if (!wrapper.contains(e.target)) {
        resultsContainer.style.display = 'none';
      }
    });
    
    // Show dropdown if input has value and is focused
    input.addEventListener('focus', () => {
      if (input.value.trim().length >= 2 && resultsContainer.children.length > 0) {
        resultsContainer.style.display = 'block';
      }
    });
  });
  
  function renderResults(data, container) {
    const products = data.resources.results.products || [];
    const pages = data.resources.results.pages || [];
    
    if (products.length === 0 && pages.length === 0) {
      container.innerHTML = '<div class="predictive-search-no-results">No results found</div>';
      container.style.display = 'block';
      return;
    }
    
    let html = '';
    
    // 1. Pages/Suggestions Group
    if (pages.length > 0) {
      const homePageResult = pages.find(p => p.title.toLowerCase() === 'home page');
      const otherPages = pages.filter(p => p.title.toLowerCase() !== 'home page');
      
      let pagesHtml = '';
      
      if (homePageResult) {
        pagesHtml += `
          <a href="${homePageResult.url}" class="predictive-search-page-item">
            ${escapeHTML(homePageResult.title)}
          </a>
        `;
      }
      
      if (otherPages.length > 0) {
        pagesHtml += otherPages.map(page => `
          <a href="${page.url}" class="predictive-search-page-item" style="display: block; margin-top: 8px;">
            ${escapeHTML(page.title)}
          </a>
        `).join('');
      }
      
      html += `
        <div class="predictive-search-group">
          ${pagesHtml}
        </div>
      `;
    }
    
    // 2. Products Group
    if (products.length > 0) {
      html += `
        <div class="predictive-search-group">
          <div class="predictive-search-title">Products</div>
          <div class="predictive-search-product-list">
            ${products.map(product => {
              const hasMultiplePrices = product.price_min !== product.price_max;
              const formattedPrice = formatMoney(product.price_min, window.theme.moneyFormat);
              const displayPrice = hasMultiplePrices ? `From ${formattedPrice}` : formattedPrice;
              const imageUrl = product.image || '';
              
              return `
                <a href="${product.url}" class="predictive-search-product-item">
                  ${imageUrl ? `<img src="${imageUrl}" class="predictive-search-product-image" alt="${escapeHTML(product.title)}">` : '<div class="predictive-search-product-image" style="background:#e0d0b0;"></div>'}
                  <div class="predictive-search-product-info">
                    <div class="predictive-search-product-price">${displayPrice}</div>
                    <div class="predictive-search-product-title">${escapeHTML(product.title)}</div>
                  </div>
                </a>
              `;
            }).join('')}
          </div>
        </div>
      `;
    }
    
    container.innerHTML = html;
    container.style.display = 'block';
  }
  
  function escapeHTML(str) {
    if (!str) return '';
    return str.replace(/[&<>'"]/g, 
      tag => ({
        '&': '&amp;',
        '<': '&lt;',
        '>': '&gt;',
        "'": '&#39;',
        '"': '&quot;'
      }[tag] || tag)
    );
  }
}

/* ==========================================================================
   DRAWERS (MOBILE NAV, CART DRAWER, MODALS)
   ========================================================================== */
class Drawer {
  constructor(elId, openTriggerClass, closeTriggerClass) {
    this.drawer = document.getElementById(elId);
    if (!this.drawer) return;
    
    this.openTriggers = document.querySelectorAll(openTriggerClass);
    this.closeTriggers = document.querySelectorAll(closeTriggerClass);
    
    this.init();
  }
  
  init() {
    this.openTriggers.forEach(btn => {
      btn.addEventListener('click', (e) => {
        e.preventDefault();
        this.open();
      });
    });
    
    this.closeTriggers.forEach(btn => {
      btn.addEventListener('click', (e) => {
        e.preventDefault();
        this.close();
      });
    });
  }
  
  open() {
    this.drawer.classList.add('active');
    this.drawer.setAttribute('aria-hidden', 'false');
    document.body.style.overflow = 'hidden';
  }
  
  close() {
    this.drawer.classList.remove('active');
    this.drawer.setAttribute('aria-hidden', 'true');
    document.body.style.overflow = '';
  }
}

function initDrawers() {
  // Mobile Nav Drawer
  new Drawer('MobileNavDrawer', '.js-mobile-menu-open', '.js-mobile-menu-close');
  
  // Cart Drawer
  window.cartDrawer = {
    el: document.getElementById('CartDrawer'),
    isOpen: false,

    open: function() {
      if (!this.el) return;
      this.el.classList.add('active');
      this.el.setAttribute('aria-hidden', 'false');
      document.body.style.overflow = 'hidden';
      this.isOpen = true;
      this.refresh();
    },

    close: function() {
      if (!this.el) return;
      this.el.classList.remove('active');
      this.el.setAttribute('aria-hidden', 'true');
      document.body.style.overflow = '';
      this.isOpen = false;
    },

    refresh: function() {
      // Fetch fresh cart drawer HTML via Section Rendering API
      fetch('/?sections=cart-drawer')
        .then(r => r.json())
        .then(data => {
          const html = data['cart-drawer'];
          if (!html) return;
          const parser = new DOMParser();
          const doc = parser.parseFromString(html, 'text/html');
          // Replace only the drawer body content
          const newBody = doc.querySelector('.js-cart-drawer-items-wrapper');
          const currentBody = document.querySelector('.js-cart-drawer-items-wrapper');
          if (newBody && currentBody) {
            currentBody.innerHTML = newBody.innerHTML;
          }
          // Update subtotal in drawer
          const newSubtotal = doc.querySelector('.js-cart-subtotal');
          const currentSubtotal = document.querySelector('.js-cart-subtotal');
          if (newSubtotal && currentSubtotal) {
            currentSubtotal.textContent = newSubtotal.textContent;
          }
          initQtyButtons();
        })
        .catch(err => console.error('Cart drawer refresh error:', err));
    },

    // Keep old name for backwards compat
    updateCartHtml: function() { this.refresh(); }
  };
  
  const cartOpenTriggers = document.querySelectorAll('.js-cart-drawer-open');
  cartOpenTriggers.forEach(btn => {
    btn.addEventListener('click', (e) => {
      e.preventDefault();
      window.cartDrawer.open();
    });
  });
  
  // Generic Modal Trigger (e.g. Size Guide)
  const modalTriggers = document.querySelectorAll('.js-modal-trigger');
  modalTriggers.forEach(btn => {
    btn.addEventListener('click', (e) => {
      e.preventDefault();
      const targetId = btn.getAttribute('data-target');
      const modal = document.getElementById(targetId);
      if (modal) {
        modal.classList.add('active');
        document.body.style.overflow = 'hidden';
      }
    });
  });
  
  const modalCloseTriggers = document.querySelectorAll('.js-modal-close');
  modalCloseTriggers.forEach(btn => {
    btn.addEventListener('click', (e) => {
      e.preventDefault();
      const targetId = btn.getAttribute('data-target');
      const modal = document.getElementById(targetId);
      if (modal) {
        modal.classList.remove('active');
        document.body.style.overflow = '';
      }
    });
  });
}

// Cart Action Utility — AJAX quantity / remove
function updateCartItemQuantity(key, quantity) {
  // Optimistic loading state
  const cartPageWrapper = document.querySelector('.js-cart-items-wrapper');
  if (cartPageWrapper) cartPageWrapper.classList.add('loading');

  return fetch('/cart/change.js', {
    method: 'POST',
    headers: { 'Content-Type': 'application/json' },
    body: JSON.stringify({ id: key, quantity: quantity })
  })
  .then(r => r.json())
  .then(cart => {
    // ① Update all cart count badges
    document.querySelectorAll('.js-cart-count').forEach(el => {
      el.textContent = cart.item_count;
    });

    const drawerEl = document.getElementById('CartDrawer');
    const drawerIsOpen = drawerEl && drawerEl.classList.contains('active');

    if (drawerIsOpen) {
      // ② Refresh cart drawer content
      window.cartDrawer.refresh();
      if (cartPageWrapper) cartPageWrapper.classList.remove('loading');
    } else if (cartPageWrapper) {
      // ③ Cart page — re-render via Section Rendering API
      fetch('/?sections=main-cart')
        .then(r => r.json())
        .then(data => {
          const html = data['main-cart'];
          if (!html) { cartPageWrapper.classList.remove('loading'); return; }
          const parser = new DOMParser();
          const doc = parser.parseFromString(html, 'text/html');
          const newWrapper = doc.querySelector('.js-cart-items-wrapper');
          if (newWrapper) {
            cartPageWrapper.innerHTML = newWrapper.innerHTML;
          }
          // Update subtotal on cart page
          const newSubtotal = doc.querySelector('.js-cart-subtotal');
          const currentSubtotal = document.querySelector('.js-cart-subtotal');
          if (newSubtotal && currentSubtotal) {
            currentSubtotal.textContent = newSubtotal.textContent;
          }
          cartPageWrapper.classList.remove('loading');
          initQtyButtons();
        })
        .catch(err => {
          console.error('Cart page refresh error:', err);
          cartPageWrapper.classList.remove('loading');
          window.location.reload();
        });
    }
  })
  .catch(err => {
    console.error('updateCartItemQuantity error:', err);
    if (cartPageWrapper) cartPageWrapper.classList.remove('loading');
  });
}

function parseMoney(moneyString) {
  if (!moneyString) return 0;
  const cleanString = moneyString.replace(/[^0-9]/g, '');
  return parseInt(cleanString) || 0;
}

function updateOptimisticCart(itemEl, qtyChange, newQty) {
  if (!itemEl) return;
  
  if (newQty === 0) {
    itemEl.classList.add('removing');
  }

  const subtotalEl = document.querySelector('.js-cart-subtotal');
  if (subtotalEl) {
    let totalCents = 0;
    const allItems = document.querySelectorAll('.cart-item:not(.removing)');
    allItems.forEach(item => {
      const p = item.querySelector('.cart-item__final-price');
      const input = item.querySelector('.js-qty-input');
      if (p && input) {
        const itemUnitPrice = parseMoney(p.textContent);
        const itemQty = parseInt(input.value) || 0;
        totalCents += (itemUnitPrice * itemQty);
      }
    });
    subtotalEl.textContent = formatMoney(totalCents);
  }

  const badges = document.querySelectorAll('.js-cart-count');
  badges.forEach(badge => {
    let currentCount = 0;
    const allInputs = document.querySelectorAll('.cart-item:not(.removing) .js-qty-input');
    allInputs.forEach(inp => {
      currentCount += (parseInt(inp.value) || 0);
    });
    badge.textContent = currentCount;
  });
}

function initQtyButtons() {
  const qtyBtns = document.querySelectorAll('.js-qty-btn');
  qtyBtns.forEach(btn => {
    if (btn.getAttribute('data-listener') === 'true') return;
    btn.setAttribute('data-listener', 'true');
    
    btn.addEventListener('click', () => {
      const parentSelector = btn.closest('.quantity-selector');
      const input = parentSelector.querySelector('.js-qty-input');
      const action = btn.getAttribute('data-action');
      const itemKey = input.getAttribute('data-key');
      const itemEl = btn.closest('.cart-item');
      let currentVal = parseInt(input.value) || 0;
      let oldVal = currentVal;
      
      if (action === 'increase') {
        currentVal++;
      } else if (action === 'decrease' && currentVal > 0) {
        currentVal--;
      }
      
      if (currentVal !== oldVal) {
        input.value = currentVal;
        updateOptimisticCart(itemEl, currentVal - oldVal, currentVal);
        updateCartItemQuantity(itemKey, currentVal);
      }
    });
  });
  
  // Remove button action
  const removeBtns = document.querySelectorAll('.js-cart-item-remove');
  removeBtns.forEach(btn => {
    if (btn.getAttribute('data-listener') === 'true') return;
    btn.setAttribute('data-listener', 'true');
    
    btn.addEventListener('click', () => {
      const itemKey = btn.getAttribute('data-key');
      const itemEl = btn.closest('.cart-item');
      const input = itemEl ? itemEl.querySelector('.js-qty-input') : null;
      const currentVal = input ? (parseInt(input.value) || 0) : 1;
      
      updateOptimisticCart(itemEl, -currentVal, 0);
      updateCartItemQuantity(itemKey, 0);
    });
  });

  // Manual input update and Enter key handling
  const qtyInputs = document.querySelectorAll('.js-qty-input');
  qtyInputs.forEach(input => {
    if (input.getAttribute('data-listener') === 'true') return;
    input.setAttribute('data-listener', 'true');
    
    input.addEventListener('focus', () => {
      input.dataset.oldVal = input.value;
    });

    input.addEventListener('change', () => {
      let currentVal = parseInt(input.value);
      if (isNaN(currentVal) || currentVal < 0) {
        currentVal = 0;
      }
      input.value = currentVal;
      const itemKey = input.getAttribute('data-key');
      const itemEl = input.closest('.cart-item');
      let oldVal = parseInt(input.dataset.oldVal) || parseInt(input.value) || 0;
      
      if (currentVal !== oldVal) {
        updateOptimisticCart(itemEl, currentVal - oldVal, currentVal);
        updateCartItemQuantity(itemKey, currentVal);
      }
    });
    
    input.addEventListener('keydown', (e) => {
      if (e.key === 'Enter') {
        e.preventDefault();
        input.blur();
      }
    });
  });
}

/* ==========================================================================
   CAROUSEL SLIDERS (HERO, TESTIMONIALS)
   ========================================================================== */
function initHeroSliders() {
  const sliders = document.querySelectorAll('.js-hero-slider');
  sliders.forEach(slider => {
    const slides = slider.querySelectorAll('.js-hero-slide');
    const dots = slider.querySelectorAll('.js-hero-dot');
    const autoplay = slider.getAttribute('data-autoplay') === 'true';
    const delay = parseInt(slider.getAttribute('data-delay')) || 6000;
    
    if (slides.length <= 1) return;
    let currentSlide = 0;
    let intervalId = null;
    
    const goToSlide = (idx) => {
      slides[currentSlide].classList.remove('active');
      dots[currentSlide].classList.remove('active');
      currentSlide = idx;
      slides[currentSlide].classList.add('active');
      dots[currentSlide].classList.add('active');
    };
    
    const nextSlide = () => {
      const nextIdx = (currentSlide + 1) % slides.length;
      goToSlide(nextIdx);
    };
    
    dots.forEach(dot => {
      dot.addEventListener('click', () => {
        const targetIdx = parseInt(dot.getAttribute('data-index'));
        goToSlide(targetIdx);
        if (autoplay) {
          clearInterval(intervalId);
          startAutoplay();
        }
      });
    });
    
    const startAutoplay = () => {
      intervalId = setInterval(nextSlide, delay);
    };
    
    if (autoplay) startAutoplay();
  });
}

function initTestimonialsSliders() {
  const sliders = document.querySelectorAll('.js-testimonials-slider');
  sliders.forEach(slider => {
    const slides = slider.querySelectorAll('.js-testimonials-slide');
    const dots = slider.querySelectorAll('.js-testimonials-dot');
    const autoplay = slider.getAttribute('data-autoplay') === 'true';
    const speed = parseInt(slider.getAttribute('data-speed')) || 5000;
    
    if (slides.length <= 1) return;
    let currentSlide = 0;
    let intervalId = null;
    
    const goToSlide = (idx) => {
      slides[currentSlide].classList.remove('active');
      dots[currentSlide].classList.remove('active');
      currentSlide = idx;
      slides[currentSlide].classList.add('active');
      dots[currentSlide].classList.add('active');
    };
    
    dots.forEach(dot => {
      dot.addEventListener('click', () => {
        const targetIdx = parseInt(dot.getAttribute('data-index'));
        goToSlide(targetIdx);
        if (autoplay) {
          clearInterval(intervalId);
          intervalId = setInterval(() => {
            goToSlide((currentSlide + 1) % slides.length);
          }, speed);
        }
      });
    });
    
    if (autoplay) {
      intervalId = setInterval(() => {
        goToSlide((currentSlide + 1) % slides.length);
      }, speed);
    }
  });
}

/* ==========================================================================
   PRODUCT CARD VARIANT SELECTORS
   ========================================================================== */
function initCardVariantSelectors() {
  const cards = document.querySelectorAll('.product-card');
  cards.forEach(card => {
    const jsonEl = card.querySelector('.js-card-variants-json');
    if (!jsonEl) return;
    if (card.getAttribute('data-variants-initialized') === 'true') return;
    card.setAttribute('data-variants-initialized', 'true');

    const variants = JSON.parse(jsonEl.textContent);
    const selects = card.querySelectorAll('.js-card-option-select');
    const atcBtn = card.querySelector('.js-card-atc-btn');
    const priceWrapper = card.querySelector('.product-card__price');

    function updateCardVariant() {
      const selectedOptions = [];
      selects.forEach(select => {
        selectedOptions.push(select.value);
      });

      const matchedVariant = variants.find(variant => {
        return variant.options.every(optionVal => selectedOptions.includes(optionVal));
      });

      if (matchedVariant) {
        if (atcBtn) {
          atcBtn.setAttribute('data-variant-id', matchedVariant.id);
          if (matchedVariant.available) {
            atcBtn.disabled = false;
            atcBtn.classList.remove('disabled');
            atcBtn.textContent = window.theme.strings.addToCart || 'ADD TO CART';
          } else {
            atcBtn.disabled = true;
            atcBtn.classList.add('disabled');
            atcBtn.textContent = window.theme.strings.soldOut || 'SOLD OUT';
          }
        }

        if (priceWrapper) {
          const priceContainer = priceWrapper.querySelector('.price');
          const regularPriceItem = priceWrapper.querySelector('.price-item--regular');
          const salePriceItem = priceWrapper.querySelector('.price-item--sale');
          const comparePriceItem = priceWrapper.querySelector('.compare-at-price');
          
          if (priceContainer) {
            const formattedPrice = formatMoney(matchedVariant.price, window.theme.moneyFormat);
            priceContainer.classList.toggle('price--sold-out', !matchedVariant.available);
            
            if (matchedVariant.compare_at_price > matchedVariant.price) {
              priceContainer.classList.add('price--on-sale');
              if (salePriceItem) salePriceItem.textContent = formattedPrice;
              if (comparePriceItem) comparePriceItem.textContent = formatMoney(matchedVariant.compare_at_price, window.theme.moneyFormat);
            } else {
              priceContainer.classList.remove('price--on-sale');
              if (regularPriceItem) regularPriceItem.textContent = formattedPrice;
            }
          }
        }
      } else {
        if (atcBtn) {
          atcBtn.disabled = true;
          atcBtn.classList.add('disabled');
          atcBtn.textContent = 'UNAVAILABLE';
        }
      }
    }

    selects.forEach(select => {
      select.addEventListener('change', updateCardVariant);
    });
  });
}

/* ==========================================================================
   QUICK ADD CARDS SIZE SELECTORS
   ========================================================================== */
function initQuickAdd() {
  initCardVariantSelectors();
  const addBtns = document.querySelectorAll('.js-quick-add-btn');
  addBtns.forEach(btn => {
    if (btn.getAttribute('data-listener') === 'true') return;
    btn.setAttribute('data-listener', 'true');

    btn.addEventListener('click', (e) => {
      e.preventDefault();
      const variantId = btn.getAttribute('data-variant-id');
      if (!variantId) return;

      btn.disabled = true;
      const isSizeBtn = btn.classList.contains('quick-add-size-btn');
      const originalText = btn.textContent;
      if (!isSizeBtn) {
        btn.textContent = 'ADDING...';
      }

      fetch('/cart/add.js', {
        method: 'POST',
        headers: { 'Content-Type': 'application/json' },
        body: JSON.stringify({ items: [{ id: variantId, quantity: 1 }] })
      })
      .then(r => r.json())
      .then(data => {
        if (!isSizeBtn) {
          btn.textContent = originalText;
        }
        btn.disabled = false;

        // Update cart count
        return fetch('/cart.js')
          .then(r => r.json())
          .then(cart => {
            document.querySelectorAll('.js-cart-count').forEach(el => {
              el.textContent = cart.item_count;
            });
            // Open and refresh drawer
            if (window.cartDrawer) {
              window.cartDrawer.open();
            }
          });
      })
      .catch(err => {
        console.error('Quick add error:', err);
        if (!isSizeBtn) {
          btn.textContent = originalText;
        }
        btn.disabled = false;
      });
    });
  });
}

/* ==========================================================================
   PRODUCT PAGE MODULES (VARIANT INFERENCE, GALLERY SELECTS)
   ========================================================================== */
function initProductPage() {
  const pageWrapper = document.querySelector('.js-product-page-wrapper');
  const productId = pageWrapper.getAttribute('data-product-id');
  const variantsJsonEl = document.getElementById(`ProductVariants-${productId}`);
  if (!variantsJsonEl) return;
  
  const variants = JSON.parse(variantsJsonEl.textContent);
  
  // Gallery thumbnails (desktop)
  const thumbs = pageWrapper.querySelectorAll('.js-gallery-thumb');
  const mainItems = pageWrapper.querySelectorAll('.js-gallery-item');
  const galleryMain = pageWrapper.querySelector('.js-product-gallery-main');
  const dots = pageWrapper.querySelectorAll('.js-gallery-dot');
  const arrows = pageWrapper.querySelectorAll('.js-gallery-arrow');
  let currentIdx = 0;

  function setActiveSlide(index) {
    currentIdx = index;
    // Desktop: show/hide items
    mainItems.forEach((item, i) => {
      item.classList.toggle('active', i === index);
    });
    thumbs.forEach((t, i) => {
      t.classList.toggle('active', i === index);
    });
    dots.forEach((d, i) => {
      d.classList.toggle('active', i === index);
    });
  }

  // Desktop thumbnail click
  thumbs.forEach((thumb, idx) => {
    thumb.addEventListener('click', () => {
      setActiveSlide(idx);
      // Also scroll gallery on mobile if needed
      if (galleryMain && window.innerWidth < 768) {
        galleryMain.scrollTo({ left: galleryMain.offsetWidth * idx, behavior: 'smooth' });
      }
    });
  });

  // Desktop gallery arrows click
  arrows.forEach((arrow) => {
    arrow.addEventListener('click', () => {
      const action = arrow.getAttribute('data-action');
      let newIdx = currentIdx;
      if (action === 'next') {
        newIdx = (currentIdx + 1) % mainItems.length;
      } else if (action === 'prev') {
        newIdx = (currentIdx - 1 + mainItems.length) % mainItems.length;
      }
      setActiveSlide(newIdx);
      if (galleryMain && window.innerWidth < 768) {
        galleryMain.scrollTo({ left: galleryMain.offsetWidth * newIdx, behavior: 'smooth' });
      }
    });
  });

  // Mobile dot tap — scroll to slide
  dots.forEach((dot) => {
    dot.addEventListener('click', () => {
      const idx = parseInt(dot.getAttribute('data-index'), 10);
      if (galleryMain) {
        galleryMain.scrollTo({ left: galleryMain.offsetWidth * idx, behavior: 'smooth' });
      }
      setActiveSlide(idx);
    });
  });

  // Mobile scroll → sync dots & thumbnails
  if (galleryMain) {
    let scrollTimer = null;
    galleryMain.addEventListener('scroll', () => {
      clearTimeout(scrollTimer);
      scrollTimer = setTimeout(() => {
        const idx = Math.round(galleryMain.scrollLeft / galleryMain.offsetWidth);
        setActiveSlide(idx);
      }, 80);
    }, { passive: true });
  }
  
  // Page Quantity Selectors (+/- buttons)
  const pageQtyInputs = pageWrapper.querySelectorAll('.js-page-qty-input');
  pageQtyInputs.forEach(input => {
    const parent = input.closest('.quantity-selector');
    const btns = parent.querySelectorAll('.js-page-qty-btn');
    btns.forEach(btn => {
      btn.addEventListener('click', () => {
        const action = btn.getAttribute('data-action');
        let val = parseInt(input.value) || 1;
        if (action === 'increase') {
          val++;
        } else if (action === 'decrease' && val > 1) {
          val--;
        }
        input.value = val;
      });
    });
  });
  
  // Variant Radio Picker event listener
  const formRadios = pageWrapper.querySelectorAll('.js-option-radio, .swatch-input');
  formRadios.forEach(radio => {
    radio.addEventListener('change', () => {
      // Update displayed option label
      const optionContainer = radio.closest('.product-form__option');
      const valDisplay = optionContainer.querySelector('.js-option-value-display');
      if (valDisplay) {
        valDisplay.textContent = radio.value;
      }
      
      updateSelectedVariant(pageWrapper, variants);
    });
  });

  // Variant Select Dropdown event listener
  const formSelects = pageWrapper.querySelectorAll('.js-option-select');
  formSelects.forEach(select => {
    select.addEventListener('change', () => {
      updateSelectedVariant(pageWrapper, variants);
    });
  });
  
  // AJAX Add to Cart on Main Product
  const productForm = pageWrapper.querySelector('.js-product-form');
  if (productForm) {
    productForm.addEventListener('submit', (e) => {
      // Check if clicked button is the dynamic checkout (Buy Now) button
      const submitter = e.submitter || document.activeElement;
      if (submitter && (submitter.classList.contains('shopify-payment-button__button') || submitter.closest('.shopify-payment-button'))) {
        return; // Allow standard Buy Now checkout redirect
      }

      e.preventDefault();

      const submitBtn = productForm.querySelector('.js-add-to-cart-btn');
      const variantIdInput = productForm.querySelector('.js-variant-id-input');
      const qtyInput = productForm.querySelector('.js-page-qty-input');

      const variantId = variantIdInput ? variantIdInput.value : null;
      const quantity = qtyInput ? parseInt(qtyInput.value) || 1 : 1;

      if (!variantId || !submitBtn) return;

      submitBtn.disabled = true;
      const btnText = submitBtn.querySelector('.js-btn-text');
      const originalText = btnText ? btnText.textContent : submitBtn.textContent;
      
      if (btnText) {
        btnText.textContent = 'ADDING...';
      } else {
        submitBtn.textContent = 'ADDING...';
      }

      fetch('/cart/add.js', {
        method: 'POST',
        headers: { 'Content-Type': 'application/json' },
        body: JSON.stringify({ items: [{ id: variantId, quantity: quantity }] })
      })
      .then(r => r.json())
      .then(() => {
        if (btnText) {
          btnText.textContent = originalText;
        } else {
          submitBtn.textContent = originalText;
        }
        submitBtn.disabled = false;

        // Refresh cart count badge
        return fetch('/cart.js')
          .then(r => r.json())
          .then(cart => {
            document.querySelectorAll('.js-cart-count').forEach(el => {
              el.textContent = cart.item_count;
            });
            // Open cart drawer and refresh its contents
            if (window.cartDrawer) {
              window.cartDrawer.open();
            }
          });
      })
      .catch(err => {
        console.error('Add to cart error:', err);
        if (btnText) {
          btnText.textContent = originalText;
        } else {
          submitBtn.textContent = originalText;
        }
        submitBtn.disabled = false;
      });
    });
  }

  // Sticky Buy Now / Add to Cart Button Visibility & Instant Checkout
  const stickyBuyBar = document.querySelector('.js-product-sticky-buy');
  const mainAddToCartBtn = pageWrapper.querySelector('.js-add-to-cart-btn') || pageWrapper.querySelector('.product-form__actions');
  const stickyBuyBtn = stickyBuyBar ? stickyBuyBar.querySelector('.js-sticky-buy-btn') : null;

  if (stickyBuyBar && mainAddToCartBtn) {
    function updateStickyVisibility() {
      const rect = mainAddToCartBtn.getBoundingClientRect();
      const windowHeight = window.innerHeight || document.documentElement.clientHeight;

      // Check if the main Add to Cart button is currently visible in the viewport
      const isMainButtonVisible = rect.top < windowHeight && rect.bottom > 0;

      // Keep sticky add to cart visible; hide ONLY if main add to cart button is visible
      if (isMainButtonVisible) {
        stickyBuyBar.classList.remove('is-visible');
        stickyBuyBar.setAttribute('aria-hidden', 'true');
      } else {
        stickyBuyBar.classList.add('is-visible');
        stickyBuyBar.setAttribute('aria-hidden', 'false');
      }
    }

    let isTicking = false;
    const onScrollOrResize = () => {
      if (!isTicking) {
        window.requestAnimationFrame(() => {
          updateStickyVisibility();
          isTicking = false;
        });
        isTicking = true;
      }
    };

    window.addEventListener('scroll', onScrollOrResize, { passive: true });
    window.addEventListener('resize', onScrollOrResize, { passive: true });
    window.addEventListener('load', updateStickyVisibility, { once: true });

    // Initial check on load
    updateStickyVisibility();

    // Instant Checkout on Sticky Buy Click
    if (stickyBuyBtn) {
      stickyBuyBtn.addEventListener('click', async (e) => {
        e.preventDefault();
        const variantInput = pageWrapper.querySelector('.js-variant-id-input');
        const variantId = variantInput ? variantInput.value : null;
        const qtyInput = pageWrapper.querySelector('.js-page-qty-input');
        const quantity = qtyInput ? parseInt(qtyInput.value) || 1 : 1;

        if (!variantId) return;

        // Prevent double click during checkout redirect without changing text or turning grey
        stickyBuyBtn.style.pointerEvents = 'none';
        stickyBuyBtn.style.opacity = '0.85';

        try {
          const res = await fetch('/cart/add.js', {
            method: 'POST',
            headers: { 'Content-Type': 'application/json' },
            body: JSON.stringify({ id: variantId, quantity: quantity })
          });
          if (res.ok) {
            window.location.href = '/checkout';
          } else {
            const mainForm = pageWrapper.querySelector('.js-product-form');
            if (mainForm) {
              mainForm.submit();
            } else {
              stickyBuyBtn.style.pointerEvents = '';
              stickyBuyBtn.style.opacity = '';
            }
          }
        } catch (err) {
          window.location.href = '/checkout';
        }
      });
    }
  }
}

function updateSelectedVariant(pageWrapper, variants) {
  // Collect selected values
  const optionElements = pageWrapper.querySelectorAll('.product-form__option');
  const selectedOptions = [];
  
  optionElements.forEach(optionEl => {
    // Check for select dropdown first
    const selectInput = optionEl.querySelector('.js-option-select');
    if (selectInput) {
      selectedOptions.push(selectInput.value);
      return;
    }
    // Fallback to radio/swatch inputs
    const checkedInput = optionEl.querySelector('input:checked');
    if (checkedInput) {
      selectedOptions.push(checkedInput.value);
    }
  });
  
  // Find matching variant
  const matchedVariant = variants.find(variant => {
    return variant.options.every(optionVal => selectedOptions.includes(optionVal));
  });
  
  const submitBtn = pageWrapper.querySelector('.js-add-to-cart-btn');
  const variantIdInput = pageWrapper.querySelector('.js-variant-id-input');
  const priceWrapper = pageWrapper.querySelector('.js-product-price-wrapper');
  const btnText = submitBtn.querySelector('.js-btn-text');

  const hasVariants = pageWrapper.querySelector('.product-form__variants') ? true : false;
  
  if (matchedVariant) {
    // Update hidden variant input ID
    variantIdInput.value = matchedVariant.id;
    
    // Update button states
    if (matchedVariant.available) {
      submitBtn.disabled = false;
      btnText.textContent = window.theme.strings.addToCart || 'Add to Cart';
    } else {
      submitBtn.disabled = true;
      btnText.textContent = window.theme.strings.soldOut || 'Sold Out';
    }
    
    // Update pricing displays
    const priceWrappers = pageWrapper.querySelectorAll('.js-product-price-wrapper');
    const formattedPrice = formatMoney(matchedVariant.price, window.theme.moneyFormat);

    priceWrappers.forEach(pw => {
      const priceContainer = pw.querySelector('.price');
      const regularPriceItem = pw.querySelector('.price-item--regular');
      const salePriceItem = pw.querySelector('.price-item--sale');
      const comparePriceItem = pw.querySelector('.compare-at-price');
      
      if (matchedVariant.compare_at_price > matchedVariant.price) {
        if (priceContainer) priceContainer.classList.add('price--on-sale');
        if (salePriceItem) salePriceItem.textContent = formattedPrice;
        if (comparePriceItem) comparePriceItem.textContent = formatMoney(matchedVariant.compare_at_price, window.theme.moneyFormat);
      } else {
        if (priceContainer) priceContainer.classList.remove('price--on-sale');
        if (regularPriceItem) regularPriceItem.textContent = formattedPrice;
      }
    });

    // Update urgency stock message
    const urgencyEl = pageWrapper.querySelector('.js-product-urgency');
    const urgencyText = pageWrapper.querySelector('.js-urgency-text');
    if (urgencyEl && urgencyText) {
      const qty = matchedVariant.inventory_quantity;
      const tracked = matchedVariant.inventory_management === 'shopify';
      if (tracked && qty > 0 && qty <= 10) {
        urgencyText.textContent = 'There are ' + qty + ' products left';
        urgencyEl.style.display = '';
      } else {
        urgencyEl.style.display = 'none';
      }
    }

    // Sync Sticky Buy Now Bar
    const stickyBuyBar = document.querySelector('.js-product-sticky-buy');
    if (stickyBuyBar) {
      const stickyPrice = stickyBuyBar.querySelector('.js-sticky-buy-price');
      const stickyVariant = stickyBuyBar.querySelector('.js-sticky-buy-variant');
      const stickyBtn = stickyBuyBar.querySelector('.js-sticky-buy-btn');
      const stickyBtnText = stickyBuyBar.querySelector('.js-sticky-btn-text');

      if (stickyPrice) stickyPrice.textContent = formattedPrice;
      if (stickyVariant) {
        stickyVariant.textContent = hasVariants ? matchedVariant.title : '';
      }
      if (stickyBtn) {
        if (matchedVariant.available) {
          stickyBtn.disabled = false;
          if (stickyBtnText) stickyBtnText.textContent = 'BUY NOW';
        } else {
          stickyBtn.disabled = true;
          if (stickyBtnText) stickyBtnText.textContent = window.theme.strings.soldOut || 'Sold Out';
        }
      }
    }
  } else {
    submitBtn.disabled = true;
    btnText.textContent = 'Unavailable';
  }
}

// Shopify Money Formatter Helper - Guarantees clean Rupee (Rs.) formatting everywhere
function formatMoney(cents, format) {
  if (typeof cents === 'string') {
    cents = cents.replace(/[^0-9]/g, '');
  }
  if (isNaN(cents) || cents == null || cents === '') return 'Rs. 0.00';
  
  let val = (Number(cents) / 100).toFixed(2);
  let parts = val.split('.');
  parts[0] = parts[0].replace(/\B(?=(\d{3})+(?!\d))/g, ',');
  return 'Rs. ' + parts[0] + '.' + parts[1];
}

/* ==========================================================================
   COLLECTION FILTERING & SORT EVENTS
   ========================================================================== */
function initCollectionFilters() {
  const sortSelect = document.querySelector('.js-sort-by');
  const filterInputs = document.querySelectorAll('.js-filter-input');
  const filterForm = document.querySelector('.collection-filters');
  
  // Mobile-only exclusive accordion behavior: only one filter group open at a time on mobile
  if (filterForm) {
    const filterGroups = filterForm.querySelectorAll('details.collection-filters__group');

    const enforceMobileSingleOpen = () => {
      const isMobile = window.innerWidth <= 767;
      if (!isMobile) return;

      // On mobile, if multiple filter groups are open, keep only the first open
      let firstOpenFound = false;
      filterGroups.forEach((group) => {
        if (group.hasAttribute('open')) {
          if (!firstOpenFound) {
            firstOpenFound = true;
          } else {
            group.removeAttribute('open');
          }
        }
      });
    };

    enforceMobileSingleOpen();

    filterGroups.forEach(group => {
      const summary = group.querySelector('.collection-filters__header');
      if (!summary) return;

      summary.addEventListener('click', () => {
        const isMobile = window.innerWidth <= 767;
        if (!isMobile) return;

        // If the user clicked to OPEN this group (currently closed before event completes)
        if (!group.hasAttribute('open')) {
          // Close all other filter groups
          filterGroups.forEach(otherGroup => {
            if (otherGroup !== group) {
              otherGroup.removeAttribute('open');
            }
          });
        }
      });
    });

    window.addEventListener('resize', () => {
      enforceMobileSingleOpen();
    }, { passive: true });
  }
  
  const submitFilters = () => {
    const urlParams = new URLSearchParams(window.location.search);
    
    // Process checked filters
    if (filterForm) {
      const formData = new FormData(filterForm);
      // Clear current filter params
      for (const key of Array.from(urlParams.keys())) {
        if (key.startsWith('filter.')) {
          urlParams.delete(key);
        }
      }
      // Re-append checkbox parameters
      formData.forEach((value, key) => {
        urlParams.append(key, value);
      });
    }
    
    // Sort parameter
    if (sortSelect) {
      urlParams.set('sort_by', sortSelect.value);
    }
    
    window.location.search = urlParams.toString();
  };
  
  if (sortSelect) {
    sortSelect.addEventListener('change', submitFilters);
  }
  
  filterInputs.forEach(input => {
    input.addEventListener('change', submitFilters);
  });
}

/* ==========================================================================
   PAGE TRANSITIONS & PRELOADING
   ========================================================================== */
function initPageTransitions() {
  const overlay = document.querySelector('.page-transition-overlay');
  if (overlay) {
    overlay.classList.add('hidden');
  }

  // Prefetch local anchors on link hover for instant page loading
  document.querySelectorAll('a').forEach(link => {
    const href = link.getAttribute('href');
    if (!href || href.startsWith('#') || href.startsWith('mailto:') || href.startsWith('tel:') || link.target === '_blank') return;
    if (!href.startsWith('/') && !href.startsWith(window.location.origin)) return;

    link.addEventListener('mouseenter', () => {
      let prefetchLink = document.getElementById('prefetch-' + href.replace(/[^a-zA-Z0-9]/g, '-'));
      if (!prefetchLink) {
        prefetchLink = document.createElement('link');
        prefetchLink.id = 'prefetch-' + href.replace(/[^a-zA-Z0-9]/g, '-');
        prefetchLink.rel = 'prefetch';
        prefetchLink.href = href;
        document.head.appendChild(prefetchLink);
      }
    }, { passive: true });
  });

  // Instant touchstart response for account icon on mobile
  document.querySelectorAll('.header-account-btn, .header-mobile-account').forEach(btn => {
    btn.addEventListener('touchstart', () => {
      const href = btn.getAttribute('href');
      if (href) {
        window.location.href = href;
      }
    }, { passive: true });
  });
}

/* ==========================================================================
   LIGHTWEIGHT SMOOTH INERTIA SCROLL
   ========================================================================== */
function initInertiaScroll() {
  // Exclude touch-only devices with native momentum scrolling
  if (window.matchMedia('(hover: none)').matches) return;

  let targetY = window.scrollY;
  let currentY = window.scrollY;
  let isRunning = false;
  const ease = 0.085;

  function lerp(start, end, amt) {
    return (1 - amt) * start + amt * end;
  }

  function updateScroll() {
    currentY = lerp(currentY, targetY, ease);
    if (Math.abs(currentY - targetY) < 0.5) {
      currentY = targetY;
      window.scrollTo(0, currentY);
      isRunning = false;
      return;
    }
    window.scrollTo(0, currentY);
    requestAnimationFrame(updateScroll);
  }

  window.addEventListener('wheel', (e) => {
    if (e.ctrlKey) return; // Ignore pinch zoom
    e.preventDefault();

    const maxScroll = document.documentElement.scrollHeight - window.innerHeight;
    targetY += e.deltaY * 0.85;
    targetY = Math.max(0, Math.min(targetY, maxScroll));

    if (!isRunning) {
      isRunning = true;
      requestAnimationFrame(updateScroll);
    }
  }, { passive: false });

  // Update scroller targets if page is scrolled through keys or scrollbars
  window.addEventListener('scroll', () => {
    if (!isRunning) {
      targetY = window.scrollY;
      currentY = window.scrollY;
    }
  }, { passive: true });
}

/* ==========================================================================
   SCROLL STORYTELLING
   ========================================================================== */
function initScrollStorytelling() {
  const storySection = document.querySelector('.js-scroll-story');
  if (!storySection) return;

  const track = storySection.querySelector('.js-scroll-story-track');
  const mediaItems = storySection.querySelectorAll('.js-story-media');
  const cards = storySection.querySelectorAll('.js-story-card');
  const stepDots = storySection.querySelectorAll('.js-story-step-dot');
  const progressBar = storySection.querySelector('.js-story-progress-bar');
  const totalStages = mediaItems.length;

  function updateStoryProgress() {
    const rect = track.getBoundingClientRect();
    const startY = rect.top + window.scrollY;
    const sectionHeight = rect.height;
    const scrollRange = sectionHeight - window.innerHeight;
    
    let progress = (window.scrollY - startY) / scrollRange;
    progress = Math.max(0, Math.min(progress, 1));
    
    if (progressBar) {
      progressBar.style.setProperty('--story-progress', `${progress * 100}%`);
    }
    
    // Compute exact active slide index
    let activeIndex = Math.floor(progress * totalStages);
    if (activeIndex >= totalStages) activeIndex = totalStages - 1;
    if (activeIndex < 0) activeIndex = 0;

    mediaItems.forEach((media, idx) => {
      if (idx === activeIndex) {
        media.classList.add('active');
      } else {
        media.classList.remove('active');
      }
    });

    cards.forEach((card, idx) => {
      if (idx === activeIndex) {
        card.classList.add('active');
      } else {
        card.classList.remove('active');
      }
    });

    stepDots.forEach((dot, idx) => {
      if (idx === activeIndex) {
        dot.classList.add('active');
      } else {
        dot.classList.remove('active');
      }
    });
  }

  // Scroll event binding
  window.addEventListener('scroll', () => {
    requestAnimationFrame(updateStoryProgress);
  }, { passive: true });

  // Progress dot click jump scrolling
  stepDots.forEach((dot, idx) => {
    dot.addEventListener('click', () => {
      const rect = track.getBoundingClientRect();
      const startY = rect.top + window.scrollY;
      const sectionHeight = rect.height;
      const scrollRange = sectionHeight - window.innerHeight;
      
      const targetScroll = startY + (idx / totalStages) * scrollRange + 40;
      
      // Animate smoothly to correct height
      window.scrollTo({
        top: targetScroll,
        behavior: 'smooth'
      });
    });
  });

  // Call immediately
  updateStoryProgress();
}

/* ==========================================================================
   ADVANCED SCROLL REVEAL OBSERVER
   ========================================================================== */
function initScrollAnimations() {
  const observerOptions = {
    root: null,
    rootMargin: '0px 0px -8% 0px',
    threshold: 0.05
  };
  
  // Custom observer to handle reveal triggers
  const observer = new IntersectionObserver((entries, observer) => {
    entries.forEach(entry => {
      if (entry.isIntersecting) {
        entry.target.classList.add('reveal-visible');
        
        // Stagger children layout if exists
        const staggeredEls = entry.target.querySelectorAll('[data-reveal-child]');
        staggeredEls.forEach((child, index) => {
          child.style.setProperty('--reveal-index', index);
          child.classList.add('reveal-visible');
        });

        // Trigger stats count animation
        const counters = entry.target.querySelectorAll('[data-count-to]');
        counters.forEach(counter => {
          animateCount(counter);
        });
        
        observer.unobserve(entry.target);
      }
    });
  }, observerOptions);
  
  // Find all elements marked for reveal
  const animateEls = document.querySelectorAll('[data-reveal], .reveal-on-scroll, .reveal-on-scroll-mobile');
  animateEls.forEach(el => {
    if (el.classList.contains('reveal-on-scroll')) {
      el.setAttribute('data-reveal', 'fade-up');
    }
    observer.observe(el);
  });

  // Initialize Hero split title text parallax settings
  initHeroParallax();
}

/* ==========================================================================
   HERO HEADING SPLIT TEXT & FLOATING BEAN PARALLAX
   ========================================================================== */
function initHeroParallax() {
  const hero = document.querySelector('.hero-slider');
  if (!hero) return;

  // Split headers line-by-line
  const headings = hero.querySelectorAll('.hero-slider__title');
  headings.forEach(heading => {
    const text = heading.textContent.trim();
    heading.textContent = '';
    
    const words = text.split(' ');
    words.forEach((word, wordIdx) => {
      const spanWord = document.createElement('span');
      spanWord.className = 'split-text-word';
      spanWord.style.setProperty('--word-index', wordIdx);
      spanWord.textContent = word + ' ';
      
      const spanLine = document.createElement('span');
      spanLine.className = 'split-text-line';
      spanLine.appendChild(spanWord);
      
      heading.appendChild(spanLine);
    });
    
    // Add reveal class to heading
    heading.setAttribute('data-reveal', 'fade');
    heading.classList.add('reveal-visible');
  });



  // Smooth, inertia-based parallax calculations on mousemove using requestAnimationFrame
  const wrappers = hero.querySelectorAll('.hero-floating-bean-wrapper');
  let mouseX = 0;
  let mouseY = 0;
  let currentX = 0;
  let currentY = 0;
  const friction = 0.07; // Smoothness factor
  
  window.addEventListener('mousemove', (e) => {
    mouseX = (e.clientX / window.innerWidth) - 0.5;
    mouseY = (e.clientY / window.innerHeight) - 0.5;
  }, { passive: true });

  // Optimize performance by ticking loop only when Hero slider is visible
  let isHeroVisible = true;
  if ('IntersectionObserver' in window) {
    const observer = new IntersectionObserver((entries) => {
      entries.forEach(entry => {
        isHeroVisible = entry.isIntersecting;
      });
    }, { threshold: 0.05 });
    observer.observe(hero);
  }

  function tickParallax() {
    if (isHeroVisible && wrappers.length > 0) {
      currentX += (mouseX - currentX) * friction;
      currentY += (mouseY - currentY) * friction;
      
      wrappers.forEach((wrapper) => {
        const isType1 = wrapper.classList.contains('hero-floating-bean-wrapper--1');
        // Opposite motion vectors create stereoscopic 3D depth
        const multiplierX = isType1 ? 40 : -50;
        const multiplierY = isType1 ? 30 : -40;
        const rotMultiplier = isType1 ? 12 : -15;
        
        const tx = currentX * multiplierX;
        const ty = currentY * multiplierY;
        const rot = currentX * rotMultiplier;
        
        wrapper.style.transform = `translate3d(${tx}px, ${ty}px, 0) rotate(${rot}deg)`;
      });
    }
    requestAnimationFrame(tickParallax);
  }
  requestAnimationFrame(tickParallax);
}

/* ==========================================================================
   STATISTICS COUNT UP ANIMATOR
   ========================================================================== */
function animateCount(el) {
  const target = parseFloat(el.getAttribute('data-count-to'));
  if (isNaN(target)) return;
  
  const duration = 1800; // ms
  const start = 0;
  const startTime = performance.now();
  
  function updateNumber(now) {
    const elapsed = now - startTime;
    const progress = Math.min(elapsed / duration, 1);
    
    // Ease out cubic
    const easeProgress = 1 - Math.pow(1 - progress, 3);
    const current = Math.floor(start + easeProgress * (target - start));
    
    el.textContent = current.toLocaleString();
    
    if (progress < 1) {
      requestAnimationFrame(updateNumber);
    } else {
      el.textContent = target.toLocaleString();
    }
  }
  
  requestAnimationFrame(updateNumber);
}

/* ==========================================================================
   GLOBAL CART DISCOUNT/COUPON CONTROLLER
   ========================================================================== */
function initGlobalDiscount() {
  const savedCode = sessionStorage.getItem('shopify-discount-code');
  applyDiscountCode(savedCode);

  document.addEventListener('input', function(e) {
    if (e.target && (e.target.id === 'CartDiscountInput' || e.target.id === 'DrawerDiscountInput')) {
      const code = e.target.value.trim();
      const otherInputId = e.target.id === 'CartDiscountInput' ? 'DrawerDiscountInput' : 'CartDiscountInput';
      const otherInput = document.getElementById(otherInputId);
      if (otherInput) otherInput.value = code;
    }
  });

  document.addEventListener('keydown', function(e) {
    if (e.key === 'Enter' && e.target && (e.target.id === 'CartDiscountInput' || e.target.id === 'DrawerDiscountInput')) {
      e.preventDefault();
      const code = e.target.value.trim();
      applyDiscountCode(code);
    }
  });

  document.addEventListener('click', function(e) {
    if (e.target && (e.target.id === 'CartDiscountApply' || e.target.id === 'DrawerDiscountApply')) {
      const inputId = e.target.id === 'CartDiscountApply' ? 'CartDiscountInput' : 'DrawerDiscountInput';
      const input = document.getElementById(inputId);
      const code = input ? input.value.trim() : '';
      applyDiscountCode(code);
    }
  });

  function applyDiscountCode(code) {
    const msgs = document.querySelectorAll('#CartDiscountMsg, #DrawerDiscountMsg');
    const inputs = document.querySelectorAll('#CartDiscountInput, #DrawerDiscountInput');
    const forms = document.querySelectorAll('form[action="/cart"], form[action="/cart/add"]');

    if (code) {
      sessionStorage.setItem('shopify-discount-code', code);
      inputs.forEach(input => input.value = code);
      msgs.forEach(msg => {
        msg.textContent = `Coupon "${code}" will be applied at checkout.`;
        msg.style.color = '#25d366';
        msg.style.display = 'block';
      });
    } else {
      sessionStorage.removeItem('shopify-discount-code');
      inputs.forEach(input => input.value = '');
      msgs.forEach(msg => {
        msg.style.display = 'none';
      });
    }

    forms.forEach(form => {
      let hiddenInput = form.querySelector('input[name="discount"]');
      if (code) {
        if (!hiddenInput) {
          hiddenInput = document.createElement('input');
          hiddenInput.type = 'hidden';
          hiddenInput.name = 'discount';
          form.appendChild(hiddenInput);
        }
        hiddenInput.value = code;
      } else if (hiddenInput) {
        hiddenInput.remove();
      }
    });
  }

  // Intercept fetch API calls (like cart updates) to re-inject hidden inputs
  const originalFetch = window.fetch;
  if (originalFetch) {
    window.fetch = function() {
      return originalFetch.apply(this, arguments).then(response => {
        setTimeout(() => {
          const code = sessionStorage.getItem('shopify-discount-code');
          if (code) {
            applyDiscountCode(code);
          }
        }, 150);
        return response;
      });
    };
  }
}

document.addEventListener('DOMContentLoaded', function() {
  initGlobalDiscount();
});

/* ==========================================================================
   PRODUCT RECOMMENDATIONS DYNAMIC LOADER
   ========================================================================== */
function initProductRecommendations() {
  const container = document.querySelector('.js-recommendations');
  if (!container) return;

  const url = container.getAttribute('data-url');
  if (!url) return;

  fetch(url)
    .then(response => response.text())
    .then(html => {
      const parser = new DOMParser();
      const doc = parser.parseFromString(html, 'text/html');
      const recommendations = doc.querySelector('.js-recommendations');
      if (recommendations && recommendations.innerHTML.trim() !== '') {
        container.innerHTML = recommendations.innerHTML;
        
        // Re-initialize animations if any
        if (typeof initScrollAnimations === 'function') {
          initScrollAnimations();
        }
        // Re-initialize quick add for recommended products
        if (typeof initQuickAdd === 'function') {
          initQuickAdd();
        }
      }
    })
    .catch(err => {
      console.error('Error loading product recommendations:', err);
    });
}

/* ==========================================================================
   MEGA MENU INTERACTIONS (CLICK TO TOGGLE WITH OUTSIDE CLICK CLOSE)
   ========================================================================== */
function initMegaMenu() {
  const triggers = document.querySelectorAll('.js-mega-menu-trigger');
  
  triggers.forEach(trigger => {
    const parentItem = trigger.closest('.site-nav__item');
    const megaMenu = parentItem ? parentItem.querySelector('.js-mega-menu') : null;
    
    if (!trigger || !megaMenu) return;
    
    trigger.addEventListener('click', function(e) {
      const isOpen = megaMenu.classList.contains('is-open');
      
      // Close all other open mega menus first
      closeAllMegaMenus();
      
      if (!isOpen) {
        e.preventDefault(); // Stop click navigation
        e.stopPropagation();
        
        megaMenu.classList.add('is-open');
        megaMenu.setAttribute('aria-hidden', 'false');
        parentItem.classList.add('is-active');
        trigger.setAttribute('aria-expanded', 'true');
      } else {
        // Toggle close if clicked again
        e.preventDefault();
        e.stopPropagation();
        
        megaMenu.classList.remove('is-open');
        megaMenu.setAttribute('aria-hidden', 'true');
        parentItem.classList.remove('is-active');
        trigger.setAttribute('aria-expanded', 'false');
      }
    });
  });
  
  // Close menu when clicking outside
  document.addEventListener('click', function(e) {
    if (!e.target.closest('.site-nav__item')) {
      closeAllMegaMenus();
    }
  });
  
  // Close menu when pressing Escape key
  document.addEventListener('keydown', function(e) {
    if (e.key === 'Escape') {
      closeAllMegaMenus();
    }
  });
  
  function closeAllMegaMenus() {
    document.querySelectorAll('.js-mega-menu').forEach(menu => {
      menu.classList.remove('is-open');
      menu.setAttribute('aria-hidden', 'true');
    });
    document.querySelectorAll('.site-nav__item').forEach(item => {
      item.classList.remove('is-active');
    });
    document.querySelectorAll('.js-mega-menu-trigger').forEach(trigger => {
      trigger.setAttribute('aria-expanded', 'false');
    });
  }
}

